import React, {Suspense} from "react";
import {BrowserRouter, Routes, Route} from "react-router-dom";
import {useTheme} from "@mui/material/styles";
import {CircularProgress, Container} from "@mui/material";

import './index.css';
import ResponsiveAppBar from "./components/AppBar";
import {Footer} from "./components/Footer";
import {findToolByPath, tools} from "./tools/registry";

// Toast
import {ToastContainer} from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';

// 글자수 세기는 하위 라우트(nomal/advanced)가 있어 레지스트리에는 한 항목으로만 두고, 나머지 라우트는 여기서 직접 불러온다.
const WordCountNomal = React.lazy(() => import("./Page/WordCount/WordCountNomal"));
const WordCountAdvanced = React.lazy(() => import("./Page/WordCount/WordCountAdvanced"));

// 도구가 아닌 페이지(목록·제보·안내·404·방송 창)는 레지스트리에 없어 여기서 바로 불러온다.
const MainPage = React.lazy(() => import("./Page/MainPage"));
const ErrorReportPage = React.lazy(() => import("./Page/Report/ErrorReportPage"));
const NewFeatureSuggestPage = React.lazy(() => import("./Page/Report/NewFeatureSuggestPage"));
const UpdateNotePage = React.lazy(() => import("./Page/UpdateNotePage"));
const NotFoundPage = React.lazy(() => import("./Page/NotFoundPage"));
const CountdownDisplayPage = React.lazy(() => import("./Page/CountdownDisplayPage"));

// 토스트도 앱의 라이트/다크 모드를 그대로 따라가게 한다.
// closeOnClick/pauseOnHover/draggable을 명시해서, 페이지마다 옵션을 따로 넘기지 않아도(src/services/notify.ts)
// 지금까지와 같은 동작이 되게 한다 (react-toastify 10.0.6의 draggable 기본값은 "touch"라 true로 명시가 필요하다).
function ThemedToastContainer(): JSX.Element {
    const theme = useTheme();
    return (
        <ToastContainer
            theme={theme.palette.mode}
            position="top-right"
            autoClose={3000}
            closeOnClick
            pauseOnHover
            draggable
        />
    );
}

// 지연 로딩 중 화면 전체를 덮지 않도록, 본문 영역 안에서만 조용히 도는 작은 표시.
function RouteFallback(): JSX.Element {
    return (
        <Container sx={{display: "flex", justifyContent: "center", paddingY: 8}}>
            <CircularProgress size={24}/>
        </Container>
    );
}

export default function ServiceAppRouter(): JSX.Element {
    // 방송용 표시 창(/countdown/display)은 앱 셸(메뉴/푸터) 없이 보드만 렌더링한다.
    // 전체 페이지 이동으로 진입하므로 렌더 시점의 pathname 판단으로 충분하다.
    const isDisplayRoute = window.location.pathname.startsWith('/countdown/display');

    // 하위 라우트(nomal/advanced)가 있어 레지스트리에는 한 항목으로만 두고, 중첩 라우트 구성은 여기서 직접 만든다.
    const wordCountTool = findToolByPath('/wordcount')!;

    return (
        <BrowserRouter>
            {isDisplayRoute ? (
                <Suspense fallback={<RouteFallback/>}>
                    <Routes>
                        <Route path="/countdown/display" element={<CountdownDisplayPage/>}/>
                        <Route path="*" element={<NotFoundPage/>}/>
                    </Routes>
                </Suspense>
            ) : (
                <div style={{minHeight: "100vh", display: "flex", flexDirection: "column"}}>
                    <ResponsiveAppBar/>
                    <div style={{display: "flex", flexGrow: 1}}>
                        <Suspense fallback={<RouteFallback/>}>
                            <Routes>
                                {/* Main */}
                                <Route path="/" element={<MainPage/>}/>
                                {/* Report Page */}
                                <Route path="/report/error" element={<ErrorReportPage/>}/>
                                <Route path="/report/newfeature" element={<NewFeatureSuggestPage/>}/>
                                {/* Tools */}
                                <Route path={wordCountTool.to} element={<wordCountTool.component/>}>
                                    <Route index element={<WordCountNomal/>}/>
                                    <Route path="nomal" element={<WordCountNomal/>}/>
                                    <Route path="advanced" element={<WordCountAdvanced/>}/>
                                </Route>
                                {tools.filter((tool) => tool !== wordCountTool).map((tool) => (
                                    <Route key={tool.to} path={tool.to} element={<tool.component/>}/>
                                ))}
                                {/* etc */}
                                <Route path="/update" element={<UpdateNotePage/>}/>
                                <Route path="*" element={<NotFoundPage/>}/>
                            </Routes>
                        </Suspense>
                    </div>
                    <ThemedToastContainer/>
                    <Footer/>
                </div>
            )}
        </BrowserRouter>
    );
}
